import {ProjectTime, Record, ReportValidations, ValidationResult, ValidationResultRecord, WorkingTime} from "@/shared/timr";
import {htmlEscape} from "../utils/string";
import {formatDate, formatTime, parseISODate, parseISOZonedDateTime} from "@/shared/utils/datefunctions";
import {sortBySeverity} from "@/shared/validations/validationUtil";
import {isValidationRelevant} from "@/shared/securityHelper";

export interface ValidationsListItem {
	recordId: number; // used to show a custom message for overlap validations
	validations: ReportValidations;
	/**
	 * When true, the validation's date is appended to the message text (e.g. "... (13.07.2026)").
	 * Used in the working time date span dialog where the same validation can appear once per day
	 * and would otherwise be indistinguishable.
	 */
	showValidationDate?: boolean;
}

export function buildValidationsListItem(recordId: number, validations: ValidationResult[], showValidationDate = false): ValidationsListItem {
	return {
		recordId,
		validations: {
			relevant: validations.filter(validation => isValidationRelevant(validation)),
			irrelevant: validations.filter(validation => !isValidationRelevant(validation))
		},
		showValidationDate
	};
}

/**
 * Custom validations list web component that is used in record- and record details dialog
 * It shows a list of "alerts" in different styles based on the validation severity
 * Implementable by <validations-list-dialog></validations-list-dialog> html
 * Data can be updated by calling <code>set validationsListItem</code> on the web component
 * Can be selected via <code>document.getElementById(...) as ValidationsListDialogComponent</code>
 */
export class ValidationsListDialogComponent extends HTMLElement {
	private _validationListItem: ValidationsListItem;

	constructor() {
		super();
	}

	set validationsListItem(validationsListItem: ValidationsListItem) {
		this._validationListItem = validationsListItem;
		this.updateData();
	}

	get validationsListItem() {
		return this._validationListItem;
	}

	private updateData() {
		const validationsAvailable = this._validationListItem &&
			(this._validationListItem.validations.relevant.length > 0 || this._validationListItem.validations.irrelevant.length > 0);

		// empty container
		this.innerHTML = "";

		// append validations alerts if available
		if (validationsAvailable) {
			this.setValidationAlertFields();
		}
	}

	private setValidationAlertFields() {
		if (!this._validationListItem) {
			return;
		}

		// add relevant validations to map (OPEN of current user, OPEN/ACKNOWLEDGED of other users)
		for (const validation of sortBySeverity(this.validationsListItem.validations.relevant)) {
			this.append(this.buildValidationAlert(this.validationsListItem.recordId, true, validation));

		}

		// show irrelevant validations in grey style (CONFIRMED/CLOSED)
		for (const validation of sortBySeverity(this._validationListItem.validations.irrelevant)) {
			this.append(this.buildValidationAlert(this.validationsListItem.recordId, false, validation));
		}
	}

	/**
	 * Builds a validation alert. Based on it's severity and if relevance a different style is used
	 */
	private buildValidationAlert(id: number, relevant: boolean, validation: ValidationResult): HTMLElement {
		const alert = document.createElement("div");
		alert.classList.add("alert", `alert-validation-severity-${validation.severity}`);
		alert.classList.toggle("alert-validation-irrelevant", !relevant); // show validation grey if it's not relevant

		if (validation.messageCode === "validationResult.entryOverlap.workingTime" || validation.messageCode === "validationResult.entryOverlap.projectTime") {
			alert.insertAdjacentHTML("beforeend", this.buildOverlapValidationAlert(id, validation));
		} else {
			alert.append(this.buildValidationMessage(validation));
		}
		return alert;
	}

	/**
	 * Builds the validation message text. When <code>showValidationDate</code> is set on the list item,
	 * the validation's date is appended in parentheses (e.g. "... (13.07.2026)") so that the same
	 * validation on different days can be told apart.
	 */
	private buildValidationMessage(validation: ValidationResult): string {
		const message = messages.withArguments(validation.messageCode, ...validation.messageArguments);
		if (this._validationListItem?.showValidationDate && validation.day) {
			const date = parseISODate(validation.day);
			if (date) {
				return `${message} (${formatDate(date)})`;
			}
		}
		return message;
	}

	/**
	 * Builds validation for overlaps e.g. "Overlaps with {wtt/task name} 09:00 -> 12:00"
	 */
	private buildOverlapValidationAlert(
		id: number,
		validation: ValidationResult
	): string {
		const reference: ValidationResultRecord = [validation.recordedEntry, validation.recordedEntryRef].find(v => v.id !== id);
		const start = parseISOZonedDateTime(reference?.start);
		const end = parseISOZonedDateTime(reference?.end);
		const showTimeZone = globals.showTimeZone || start.offset !== end.offset;

		return `${messages["validationResult.entryOverlap.dialog"]}
				<i>
					${this.getOverlapRecordName(validation, reference)},
					<span>
						${formatTime(start)}
						<span class="text-timezone" ${!showTimeZone ? "style='display:none'" : ""}>${start.offset}</span>
						<i class="time-separation-symbol ml-5 mr-5"></i>
						${formatTime(end)}
						<span class="text-timezone" ${!showTimeZone ? "style='display:none'" : ""}>${end.offset}</span>
					</span>
				</i>`;
	}

	/**
	 * Returns working time type or task name whether it's a working or project time
	 * @param validation
	 * @param record
	 * @private
	 */
	private getOverlapRecordName(validation: ValidationResult, record: Record | ValidationResultRecord): string {
		// todo: query should get replaced by record.type (with TIMR-7677)
		const name = validation.messageCode === "validationResult.entryOverlap.workingTime" ?
			(record as WorkingTime).workingTimeType.name :
			(record as ProjectTime).task.name;
		return htmlEscape(name);
	}
}
